import React, { useState } from 'react';
import { X, Filter, Zap, TrendingUp, Users, ArrowRight } from 'lucide-react';
import { Link } from 'react-router-dom';
import type { UseCasePublicDto } from '@clareefai/types';

interface UseCasesDialogProps {
  isOpen: boolean;
  onClose: () => void;
  useCases: UseCasePublicDto[];
  companyName: string;
  useCaseQueryString: string;
}

const ICON_CYCLE = [
  { icon: Zap, bg: 'bg-blue-100', color: 'text-blue-600' },
  { icon: TrendingUp, bg: 'bg-emerald-100', color: 'text-emerald-600' },
  { icon: Users, bg: 'bg-purple-100', color: 'text-purple-600' },
];

const COMPANY_SIZE_OPTIONS = ['1-10', '11-50', '51-200', '201-500', '501-1000', '1000+'];
const INDUSTRY_OPTIONS = ['Technology', 'Healthcare', 'Finance', 'Education', 'Retail', 'Manufacturing'];

const UseCasesDialog: React.FC<UseCasesDialogProps> = ({
  isOpen,
  onClose,
  useCases,
  companyName,
  useCaseQueryString,
}) => {
  const [filterCompanySize, setFilterCompanySize] = useState('');
  const [filterIndustry, setFilterIndustry] = useState('');
  const [filterProduct, setFilterProduct] = useState('');

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 bg-white overflow-y-auto">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-2xl font-bold text-gray-900">All Customer Use Case</h2>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <X className="w-6 h-6 text-gray-500" />
          </button>
        </div>

        {/* Filters */}
        <div className="flex flex-wrap gap-3 mb-8">
          <div className="relative">
            <select
              value={filterCompanySize}
              onChange={(e) => setFilterCompanySize(e.target.value)}
              className="appearance-none pl-9 pr-8 py-2 border border-gray-200 rounded-full text-sm bg-white hover:border-gray-300 transition-colors cursor-pointer"
            >
              <option value="">Company Size</option>
              {COMPANY_SIZE_OPTIONS.map((size) => (
                <option key={size} value={size}>{size}</option>
              ))}
            </select>
            <Filter className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          </div>
          <div className="relative">
            <select
              value={filterIndustry}
              onChange={(e) => setFilterIndustry(e.target.value)}
              className="appearance-none pl-9 pr-8 py-2 border border-gray-200 rounded-full text-sm bg-white hover:border-gray-300 transition-colors cursor-pointer"
            >
              <option value="">Industry</option>
              {INDUSTRY_OPTIONS.map((ind) => (
                <option key={ind} value={ind}>{ind}</option>
              ))}
            </select>
            <Filter className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          </div>
          <div className="relative">
            <select
              value={filterProduct}
              onChange={(e) => setFilterProduct(e.target.value)}
              className="appearance-none pl-9 pr-8 py-2 border border-gray-200 rounded-full text-sm bg-white hover:border-gray-300 transition-colors cursor-pointer"
            >
              <option value="">Product</option>
            </select>
            <Filter className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          </div>
        </div>

        {/* Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {useCases.map((uc, i) => {
            const iconConfig = ICON_CYCLE[i % ICON_CYCLE.length];
            const IconComponent = iconConfig.icon;
            return (
              <Link
                key={uc.id}
                to={`/public-use-case/${uc.id}${useCaseQueryString}`}
                onClick={onClose}
                className="bg-white rounded-2xl border border-gray-100 p-6 hover:shadow-md hover:border-blue-100 transition-all group"
              >
                <div className={`w-10 h-10 ${iconConfig.bg} rounded-xl flex items-center justify-center mb-4`}>
                  <IconComponent className={`w-5 h-5 ${iconConfig.color}`} />
                </div>
                <h3 className="text-lg font-bold text-gray-900 mb-2 group-hover:text-blue-600 transition-colors">
                  {uc.title || 'Use Case'}
                </h3>
                <p className="text-sm text-gray-600 line-clamp-3 leading-relaxed mb-4">
                  {uc.description || ''}
                </p>
                {uc.keyBenefits && uc.keyBenefits.length > 0 && (
                  <div className="flex flex-wrap gap-1.5">
                    {uc.keyBenefits.slice(0, 3).map((benefit, j) => (
                      <span
                        key={j}
                        className="px-2.5 py-1 bg-blue-50 text-blue-700 rounded-full text-xs font-medium"
                      >
                        {benefit}
                      </span>
                    ))}
                  </div>
                )}
              </Link>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default UseCasesDialog;
